/**
 * SupabaseOpportunityHistoryRepository
 * 
 * Responsabilidade: Persistência do histórico de negociações no Supabase
 * Princípios: Single Responsibility, Repository Pattern
 */

import { supabase } from '../../lib/supabase';

export interface OpportunityHistoryEvent {
    id: string;
    opportunityId: string;
    organizationId: string;
    eventType: OpportunityHistoryEventType;
    description: string;
    oldValue?: string;
    newValue?: string;
    performedBy?: string;
    createdAt: Date;
}

export type OpportunityHistoryEventType =
    | 'created'
    | 'stage_changed'
    | 'value_updated'
    | 'agent_assigned'
    | 'agent_removed'
    | 'name_updated'
    | 'observations_updated'
    | 'task_created'
    | 'task_completed'
    | 'task_uncompleted'
    | 'task_deleted'
    | 'conversation_started'
    | 'conversation_ended'
    | 'won'
    | 'lost'
    | 'reopened';

interface CreateHistoryEventDTO {
    opportunityId: string;
    organizationId: string;
    eventType: OpportunityHistoryEventType;
    description: string;
    oldValue?: string;
    newValue?: string;
    performedBy?: string;
}

interface HistoryDbRow {
    id: string;
    opportunity_id: string;
    organization_id: string;
    event_type: string;
    description: string;
    old_value: string | null;
    new_value: string | null;
    performed_by: string | null;
    created_at: string;
}

export class SupabaseOpportunityHistoryRepository {
    private static readonly TABLE_NAME = 'opportunity_history';

    /**
     * Adiciona um evento ao histórico
     */
    static async addEvent(dto: CreateHistoryEventDTO): Promise<void> {
        try {
            const { error } = await supabase
                .from(this.TABLE_NAME)
                .insert({
                    opportunity_id: dto.opportunityId,
                    organization_id: dto.organizationId,
                    event_type: dto.eventType,
                    description: dto.description,
                    old_value: dto.oldValue || null,
                    new_value: dto.newValue || null,
                    performed_by: dto.performedBy || null
                });

            if (error) {
                console.error('[OpportunityHistoryRepository.addEvent] Error:', error);
                // Não lança erro para não bloquear a operação principal
                // O histórico é secundário e não deve impedir salvamento
            }
        } catch (error) {
            console.error('[OpportunityHistoryRepository.addEvent] Unexpected error:', error);
        }
    }

    /**
     * Busca histórico de uma negociação
     */
    static async getByOpportunityId(opportunityId: string): Promise<OpportunityHistoryEvent[]> {
        try {
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .eq('opportunity_id', opportunityId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('[OpportunityHistoryRepository.getByOpportunityId] Error:', error);
                return [];
            }

            return (data || []).map(this.mapToEntity);
        } catch (error) {
            console.error('[OpportunityHistoryRepository.getByOpportunityId] Unexpected error:', error);
            return [];
        }
    }

    /**
     * Busca histórico recente de uma organização (últimos 100 eventos)
     */
    static async getRecentByOrganization(organizationId: string, limit = 100): Promise<OpportunityHistoryEvent[]> {
        try {
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .eq('organization_id', organizationId)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('[OpportunityHistoryRepository.getRecentByOrganization] Error:', error);
                return [];
            }

            return (data || []).map(this.mapToEntity);
        } catch (error) {
            console.error('[OpportunityHistoryRepository.getRecentByOrganization] Unexpected error:', error);
            return [];
        }
    }

    /**
     * Mapeia dados do banco para entidade
     */
    private static mapToEntity(row: HistoryDbRow): OpportunityHistoryEvent {
        return {
            id: row.id,
            opportunityId: row.opportunity_id,
            organizationId: row.organization_id,
            eventType: row.event_type as OpportunityHistoryEventType,
            description: row.description,
            oldValue: row.old_value || undefined,
            newValue: row.new_value || undefined,
            performedBy: row.performed_by || undefined,
            createdAt: new Date(row.created_at)
        };
    }
}
